<template>
  <transition name="ext-prompt">
    <div
      v-if="visible"
      class="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60"
      @click.self="later"
      @keydown.esc="later"
    >
      <div
        ref="dialog"
        class="w-full max-w-md rounded-2xl border border-border bg-card text-foreground shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ext-prompt-title"
        tabindex="-1"
      >
        <div class="px-6 pt-6 pb-5">
          <div class="flex items-start justify-between gap-4">
            <img src="/logo/LogoIcon.png" alt="" class="h-11 w-11 rounded-xl shrink-0" />
            <button
              type="button"
              class="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition"
              aria-label="Close"
              @click="later"
            >
              <i class="fas fa-xmark text-sm"></i>
            </button>
          </div>
          <h2 id="ext-prompt-title" class="mt-4 text-lg font-semibold tracking-tight leading-snug">
            Write Upwork proposals right on Upwork
          </h2>
          <p class="mt-1.5 text-sm text-muted-foreground leading-relaxed">
            Add Applypa to Chrome and your AI proposals come with you to every Upwork and JobStreet job.
          </p>
          <ul class="mt-4 space-y-2.5">
            <li v-for="point in points" :key="point" class="flex items-start gap-2.5 text-sm leading-relaxed">
              <i class="fas fa-check mt-1 text-xs text-[#14A800]"></i>
              <span>{{ point }}</span>
            </li>
          </ul>
        </div>
        <div class="px-6 py-4 border-t border-border bg-muted/30 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <button
            type="button"
            class="h-10 px-4 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition"
            @click="later"
          >
            Maybe later
          </button>
          <a
            :href="storeUrl"
            target="_blank"
            rel="noopener"
            class="h-10 px-5 inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition"
            @click="addToChrome"
          >
            <i class="fab fa-chrome text-sm"></i>
            Add to Chrome — it's free
          </a>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
// One-time "Add Applypa to Chrome" modal for signed-in jobseekers. Shows on the
// Dashboard or Job Filters when the server allows it (flag on, not snoozed, fewer
// than 2 dismissals, account older than a day) AND this browser can install
// extensions AND ours isn't installed. Never on top of the Job Filters tour or
// the Upgrade modal.
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import apiClient from '@/services/api';
import authStore from '@/store/auth';
import { useUpgradeModal } from '@/composables/useUpgradeModal';
import { EXTENSION_STORE_URL, canInstallExtensionHere, extensionStatus } from '@/composables/useExtensionPrompt';

const SHOW_ON_ROUTES = ['Dashboard', 'JobFilters'];
const SHOW_DELAY_MS = 1500;

const route = useRoute();
const { state: upgradeModal } = useUpgradeModal();
const storeUrl = EXTENSION_STORE_URL;
const points = [
  'A proposal written from your profile, beside every job',
  'Tell it what to change and it rewrites',
  'Autofill the form and screening questions. You always click Send',
];

const visible = ref(false);
const dialog = ref(null);
const handledThisSession = ref(false);
let timer = null;

const eligible = computed(() => {
  const user = authStore.state.user;
  return (
    !handledThisSession.value &&
    authStore.state.isLoggedIn &&
    user?.show_extension_prompt === true &&
    SHOW_ON_ROUTES.includes(route.name) &&
    !route.query.tour &&
    !upgradeModal.show
  );
});

async function maybeShow() {
  clearTimeout(timer);
  if (!eligible.value || visible.value || !canInstallExtensionHere()) return;
  // Only people without the extension. An older install updates itself.
  if ((await extensionStatus()) !== 'missing') return;
  timer = setTimeout(async () => {
    if (!eligible.value) return;
    visible.value = true;
    await nextTick();
    dialog.value?.focus();
  }, SHOW_DELAY_MS);
}

watch(eligible, (ok) => {
  if (ok) maybeShow();
  else {
    clearTimeout(timer);
    // Step aside if the Upgrade modal or tour comes up.
    if (visible.value && (upgradeModal.show || route.query.tour)) visible.value = false;
  }
}, { immediate: true });

onBeforeUnmount(() => clearTimeout(timer));

function addToChrome() {
  // Not a dismissal: if they don't finish installing, they can see it again next visit.
  handledThisSession.value = true;
  visible.value = false;
}

async function later() {
  handledThisSession.value = true;
  visible.value = false;
  try {
    const { data } = await apiClient.post('/auth/extension-prompt/dismiss/');
    const user = authStore.state.user;
    if (user) authStore.setAuth(true, { ...user, show_extension_prompt: data.show_extension_prompt });
  } catch (e) {
    // Non-fatal: it stays hidden for this session either way.
  }
}
</script>

<style scoped>
.ext-prompt-enter-active,
.ext-prompt-leave-active {
  transition: opacity 0.2s ease;
}
.ext-prompt-enter-from,
.ext-prompt-leave-to {
  opacity: 0;
}
</style>
